/**
 * SURVEY-IMPORT.MODAL
 * Import old survey (IDTree only)
 */
import Modal from "@/components/modal"
import TextInput from "@/components/text-input"
import { store } from "@/core/root-reducer"
import UploadButtonWidget from "@/widgets/upload-button.widget"
import { useEffect, useState } from "react"
import * as XLSX from "xlsx"
import LoadingModal from "./loading.modal"
import { surveyImport } from "@/redux/survey.actions"
import { formatDate } from "@/utils/format-date.utils"
import { axisGet } from "@/redux/axis.actions"
import { sortBy, uniq } from "lodash"
import { Axis, AxisState, CONFIG_AXIS_COLORS } from "@/redux/axis.types"
import { connect } from "react-redux"
import ListItem from "@/components/list-item"
import Space from "@/components/space"

interface StateProps {
  axis: AxisState
}

interface OwnProps {
  onClose: Function
}

const getFile = (p: any): File | null => {
  const x = Array.isArray(p) ? p[0] : p
  return x instanceof File ? x : x?.file instanceof File ? x.file : (x?.target?.files?.[0] ?? null)
}

type Props = StateProps & OwnProps

function SurveyImportModal(props: Props) {
  const [date, setDate] = useState(new Date())
  const [data, setData] = useState<string[][]>([])
  const [name, setName] = useState("")
  const [step, setStep] = useState(1)

  useEffect(() => {
    store.dispatch(axisGet([], []))
  }, [])

  const readSurveyFile = (file: File) =>
    new Promise<string[][]>((resolve, reject) => {
      const r = new FileReader()

      r.onload = () => {
        try {
          const buffer = r.result as ArrayBuffer
          const wb = XLSX.read(buffer, { type: "array" })
          const ws = wb.Sheets[wb.SheetNames[0]]

          const rows = (
            XLSX.utils.sheet_to_json(ws, {
              header: 1,
              blankrows: false
            }) as any[][]
          )
            .slice(1) // remove header
            .map((row) => row.slice(0, 9).map((c) => String(c ?? "").trim()))
            .filter((row) => row.some(Boolean))

          resolve(rows)
        } catch (e) {
          reject(e)
        }
      }

      r.onerror = () => reject(r.error)
      r.readAsArrayBuffer(file) // ⚠️ important pour xlsx
    })

  async function importSurvey(d: string[][]) {
    setData(d)
    const axes = uniq(d.map((row) => row[1]))
    store.dispatch(
      axisGet(
        sortBy(
          axes.map((x, i) => new Axis({ name: { fr: x }, color: CONFIG_AXIS_COLORS[i] })),
          "label"
        ),
        []
      )
    )
    console.log(d)
    setStep(2)
  }

  async function next() {
    if (name === "") {
      alert("Nom de l'enquête non renseigné")
    } else {
      setStep(3)
      const response: any = await store.dispatch(surveyImport(data, name, date, props.axis.list))
      if (response.error) setStep(1)
      else window.location.reload()
    }
  }

  return (
    <Modal
      onClose={() => props.onClose()}
      isCloseButtonVisible
      title="Importer une ancienne enquête"
      onNext={step === 2 ? () => next() : undefined}>
      <div className="flex">
        <TextInput onChange={(e) => setName(e.value)} value={name} title="Nom de l'enquête" />
        <div className="w-20" />
        <TextInput
          type="date"
          value={formatDate(date, false, true)}
          onChange={(e) => setDate(e.value)}
          title="Date de l'enquête"
        />
      </div>

      {step === 1 && (
        <div>
          <div className="mt-10">
            <b>Colonnes à importer</b>
          </div>
          <ul>
            <li>Identifiant user</li>
            <li>Axe</li>
            <li>Thème</li>
            <li>Question</li>
            <li>{`Value (sur 100) - NULL si nspp`}</li>
            <li>Réponse</li>
            <li>{`Type ("choice", "scale" ou "open")`}</li>
            <li>{`Nombre d'échelle (5 si null)`}</li>
            <li>{`Reverse ("OUI" ou null)`}</li>
          </ul>

          <div className="-m-[3px] mt-20">
            <UploadButtonWidget
              onUpload={async (payload: any) => {
                const file = getFile(payload)
                if (!file) return
                importSurvey(await readSurveyFile(file))
                setStep(2)
              }}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-20">
          <h3>Liste des axes</h3>
          {props.axis.list
            .filter((x) => x.label !== "")
            .map((axis) => (
              <ListItem key={axis.id} isSmall>
                <b style={{ color: axis.color }}>{axis.label}</b>
                <Space />
              </ListItem>
            ))}
        </div>
      )}

      {step === 3 && <LoadingModal />}
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  axis: state.axis
})

export default connect(mapStateToProps)(SurveyImportModal)
